import { Client } from "@/Common/Api/Client";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { IGetPatientPortalCommunicationsRequest } from "@/Common/Api/SharedItem/Request/IGetPatientPortalCommunicationsRequest";
import { IGetPortalCommunicationsResponse } from "@/Common/Api/SharedItem/Response/IGetPortalCommunicationsResponse";
import { ISharedItemNoteResponse } from "@/Common/Api/SharedItem/Response/ISharedItemNoteResponse";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { GetPatientOverviewResponse } from "@/Common/Models/Portal/MedfarPortal";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { ISyncPortalSharedItemResponse } from "@/Common/Models/Portal/SharedItem/SyncPortalSharedItemResponse";
import { PortalCommunicationComposite } from "@/Common/Models/PortalCommunication/PortalCommunicationComposite";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ClientResponse } from "../ClientResponse";
import { GenericResponse } from "../Dto/GenericResponse";
import { ShareItemResponse } from "./Response/SharedItemResponse";

class SharedItemClient {
    /**
     * Get a shared item by an id.
     * @param sharedItemId A id of a shared item.
     * @returns A Shared item matched to the provided shared Item's Id.
     */
    public static async getSharedItem(sharedItemId: string): Promise<ShareItemResponse> {
        const response = await Client.postAsync<ShareItemResponse>(ServerOperations.Server.Myle_Patient_Portal_GetSharedItem, {
            SharedItemId: sharedItemId,
            IncludeLinkedItem: true,
            IncludeFiles: true,
            IncludeNotes: true
        });

        if (!response.isSuccess) {
            return null;
        }

        return response.data;
    }

    /**
     * Updates the priority for a communication message.
     * @param sharedItemId The ID of the shared item.
     * @param sharedItemMessageId The ID of the shared item message.
     * @param priority The priority of the shared item message.
    */
    public static async updatePortalCommunicationPriority(sharedItemId: string, sharedItemMessageId: number, priority: SharedItemPriority): Promise<boolean> {
        const response = await Client.postAsync<void>(ServerOperations.Server.Myle_PortalCommunication_UpdateSharedItemMessagePriority, {
            SharedItemId: sharedItemId,
            SharedItemMessageId: sharedItemMessageId,
            Priority: priority
        });

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return false;
        }

        return response.isSuccess;
    }

    /**
     * Updates the category for a communication thread.
     * @param sharedItemId The ID of the shared item.
     * @param category The category of the shared item message.
    */
    public static async updatePortalCommunicationCategory(sharedItemId: string, categoryId: string): Promise<boolean> {
        const response = await Client.postAsync<void>(ServerOperations.Server.Myle_PortalCommunication_UpdateSharedItemCategory, {
            SharedItemId: sharedItemId,
            CategoryId: categoryId
        });

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return false;
        }

        return response.isSuccess;
    }

    /**
     * Insert or update a shared item and shared item message(s) and sync it to the portal.
     * @param sharedItemMessages The messages containing the shared item information.
     * @param patientContactId The contact ID of the patient to receive the message.
     * @param category - The category of the shared item.
     * @param sharedItemType - The type of the shared item.
     * @param sharedItemId The shared item ID to be updated.
     * @param subject The text containing the subject of the message.
     * @param canPatientReplyWithMessage - Indicates whether the patient can reply with a message.
     * @param ObjectId - The Object identifier of the linked item.
     */
    public static async syncUpsertSharedItemMessage(
        sharedItemMessages: SharedItemMessage[],
        patientContactId: string,
        categoryId: string,
        sharedItemType: PortalLinkedItemType,
        sharedItemId: string = null,
        subject: string = null,
        canPatientReplyWithMessage: boolean = false,
        objectId: string = null,
        includeLinkedItem: boolean = false
    ): Promise<ISyncPortalSharedItemResponse> {
        const response = await Client.postAsync<ISyncPortalSharedItemResponse>(
            ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem,
            {
                SharedItemId: sharedItemId,
                PatientContactId: patientContactId,
                Subject: subject,
                SharedItemType: sharedItemType,
                EmployeeContactId: sessionStore.getCurrentEmployee().ContactId,
                CreatedByUserId: sessionStore.userId,
                Messages: sharedItemMessages,
                PersistLinkAction: false,
                CanPatientReplyWithMessage: canPatientReplyWithMessage,
                CategoryId: categoryId,
                ObjectId: objectId,
                IncludeLinkedItem: includeLinkedItem
            }
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }

    /**
    * Marks a portal communication shared item as unread.
    * @param sharedItemIds The shared item IDs to be marked as unread.
    * @returns A boolean value if the portal communications are maked as unread successfully.
    */
    public static async MarkPortalCommunicationAsUnread(sharedItemIdsToUpdate: string[]): Promise<boolean> {
        const response = await Client.postAsync<void>(ServerOperations.Server.Myle_PortalCommunication_UpdateSharedItemMessageReadStatus, {
            SharedItemIds: sharedItemIdsToUpdate,
            MarkAsRead: false
        });

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return false;
        }

        return response.isSuccess;
    }

    /**
     * Revokes a shared Item.
     * @param sharedItemId The ID of the shared item.
     */
    public static async revokeSharedItemAsync(sharedItemId: string): Promise<void> {
        const response = await Client.postAsync(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, { SharedItemId: sharedItemId });
        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
        }
    }

    /**
     * Get a list of portal communications based on the request.
     * @param request A request to fetch portal communications.
     * @returns An object containing:
     * - `portalCommunicationComposites`: An array of `PortalCommunicationComposite` objects created from the response data.
     * - `portalCommunicationSharedItemIds`: An array of shared item IDs extracted from the portal communication composites.
     * - `totalCount`: The total count of portal communications.
     * If there are errors in the response, a popup is shown and `null` is returned.
     * If the response data does not contain portal communication composites, an object with empty arrays and a total count of 0 is returned.
     */
    public static async fetchPatientPortalCommunications(request: IGetPatientPortalCommunicationsRequest): Promise<{
        portalCommunicationComposites: PortalCommunicationComposite[]
        portalCommunicationSharedItemIds: string[],
        totalCount: number
    }> {
        const response = await Client.postAsync<IGetPortalCommunicationsResponse>(
            ServerOperations.Server.Myle_PortalCommunication_GetPatientPortalCommunications,
            request
        );

        return this.handlePortalCommunicationsResponse(response);
    }

    /**
     * Handles the response from the portal communications API and processes the data into a structured format.
     * @param response - The client response containing the portal communications data or errors.
     * @returns An object containing:
     * - `portalCommunicationComposites`: An array of `PortalCommunicationComposite` objects created from the response data.
     * - `portalCommunicationSharedItemIds`: An array of shared item IDs extracted from the portal communication composites.
     * - `totalCount`: The total count of portal communications.
     * If there are errors in the response, a popup is shown and `null` is returned.
     * If the response data does not contain portal communication composites, an object with empty arrays and a total count of 0 is returned.
     */
    private static handlePortalCommunicationsResponse(response: ClientResponse<IGetPortalCommunicationsResponse>): {
        portalCommunicationComposites: PortalCommunicationComposite[]
        portalCommunicationSharedItemIds: string[],
        totalCount: number
    } {
        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        if (response.data.PortalCommunicationComposites) {
            return {
                portalCommunicationComposites: response.data.PortalCommunicationComposites
                    .map(PortalCommunicationComposite.fromResponseModel),
                portalCommunicationSharedItemIds: response.data.PortalCommunicationComposites
                    .map(p => p.SharedItem.SharedItemId),
                totalCount: response.data.TotalCount
            };
        }

        return {
            portalCommunicationComposites: [],
            portalCommunicationSharedItemIds: [],
            totalCount: 0
        };
    }

    /**
     * Loads the portal account overview for a patient.
     * @param patientContactId The ID of the patient for whom the portal account overview is to be loaded.
     */
    public static async loadPortalAccountOverview(patientContactId: string): Promise<GetPatientOverviewResponse> {
        const response = await Client.postAsync<GetPatientOverviewResponse>(ServerOperations.Server.Portal_Proxy_GetPatientPortalProfileOverview, { PatientContactId: patientContactId });

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }

    /**
     * Sync or revoke an attachment in a shared-item.
     * @param sharedItemId The shared item's ID.
     * @param sharedItemType The shared item's type.
     * @param includeLinkedItem Indicates to sync or revoked an attachment.
     * @param objectId An attachment Id.
     */
    public static async syncOrRevokeSharedItemsAttachment(
        sharedItemId: string,
        sharedItemType: PortalLinkedItemType,
        includeLinkedItem: boolean,
        objectId: string
    ): Promise<ISyncPortalSharedItemResponse> {
        const response = await Client.postAsync<ISyncPortalSharedItemResponse>(
            ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem,
            {
                SharedItemId: sharedItemId,
                SharedItemType: sharedItemType,
                IncludeLinkedItem: includeLinkedItem,
                ObjectId: objectId
            }
        );

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }

    /**
     * Adds a note to a shared Item.
     * @param sharedItemId The ID of the shared item.
     * @param note the text to be saved
     */
    public static async saveSharedItemNote(sharedItemId: string, note: string): Promise<ISharedItemNoteResponse> {
        const response = await Client.postAsync<ISharedItemNoteResponse>(ServerOperations.Server.Myle_PortalCommunication_SaveSharedItemNote, { SharedItemId: sharedItemId, Note: note });

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data;
    }

    /**
         * Get a patient by a contact id.
         * @param patientContactId A contact id of a patient.
         * @returns A patient matched to the patient contact Id.
         */
    public static async getPatientData(patientContactId: string): Promise<Patient> {
        const response = await Client.postAsync<LoadPatientModel>(ServerOperations.Server.Myle_LoadPatient, {
            IndividualId: patientContactId,
            IncludeRelationships: true
        });

        if (!response.isSuccess) {
            return null;
        }

        return Patient.FromResponseData(response.data);
    }

    /**
     * Marks the selected portal communication items for review.
     * @param selectedSharedItemId The shared item ID to be updated.
     * @param flagForReview A flag indicating whether to mark the selected communication as flagged for review.
     */
    public static async updateFlagPortalCommunicationForReview(selectedSharedItemId: string, flagForReview: boolean): Promise<boolean> {
        const response = await Client.postAsync<void>(ServerOperations.Server.Myle_PortalCommunication_FlagSharedItemForReview, {
            SharedItemId: selectedSharedItemId,
            FlagForReview: flagForReview
        });

        if (response.errors?.any()) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.isSuccess;
    }

    /**
     * Archive/Unarchive portal communications by Ids.
     * @param sharedItemIds The portal communications Ids to archive/unarchive.
     * @param shouldArchive A boolean whether to archive/unarchive shared Items.
     * @returns A boolean value if the portal communications are archived/unarchived successfully.
     */
    public static async updatePortalCommunicationsArchive(sharedItemIds: string[], shouldArchive: boolean): Promise<boolean> {
        const response = await Client.postAsync<GenericResponse>(
            ServerOperations.Server.Myle_PortalCommunication_UpdateSharedItemArchivedStatus,
            {
                SharedItemIds: sharedItemIds,
                ShouldArchive: shouldArchive
            }
        );

        return response.isSuccess;
    }
}

export { SharedItemClient };
